import { useState, useContext, useRef, useEffect, useCallback, useMemo } from "react";
import { Grid, TextField, Autocomplete, IconButton, InputAdornment, Paper } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES, KEYBOARD_KEYS, EVENTS } from "const";
import { eventsEmitter } from "utils/events";
import { useBarcodeScanner, isScanningBarcode, restoreInputElementValue } from "helpers/BarcodeHelper";
import { ReactComponent as DeleteIcon } from "images/svg/trash.svg";
import { ReactComponent as AddIcon } from "images/svg/add-circle.svg";
import SearchIcon from "images/svg/search.svg";
import toast from "react-hot-toast";
import styles from "./NewPurchasePageStyles.module.css";
import VendorContext from "context/VendorContext";
import ProductContext from "context/ProductContext";
import ShopContext from "context/ShopContext";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import ButtonRed from "components/utils/CustomFields/ButtonRed";
import ButtonGray from "components/utils/CustomFields/ButtonGray";
import * as NumberFormatHelper from "helpers/NumberFormatHelper";
import * as PurchaseHelper from "helpers/PurchaseHelper";
import { customFilterOptions } from "helpers/SearchHelper";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import AddItemPopup from "components/popups/AddItemPopup/AddItemPopup";
import QtyTextField from "components/utils/QtyTextField/QtyTextField";
import * as ProductHelper from "helpers/ProductHelper";

const DropdownPaper = (props) => {
  const { sx, ...otherProps } = props;
  return (
    <Paper
      {...otherProps}
      sx={{
        ...sx,
        "& .MuiAutocomplete-option.Mui-focused": {
          backgroundColor: "#E50218 !important",
          color: "white !important",
        },
        "& .MuiAutocomplete-option[aria-selected='true']": {
          backgroundColor: "#E50218 !important",
          color: "white !important",
        },
        "& .MuiAutocomplete-option[aria-selected='true'].Mui-focused": {
          backgroundColor: "#E50218 !important",
          color: "white !important",
        },
        "& .MuiAutocomplete-option[data-focus='true']": {
          backgroundColor: "#E50218 !important",
          color: "white !important",
        },
        "& .MuiAutocomplete-option:hover": {
          backgroundColor: "#E50218 !important",
          color: "white !important",
        },
      }}
    >
      <div
        style={{
          display: "flex",
          padding: "8px 16px",
          fontWeight: "bold",
          borderBottom: "1px solid #e0e0e0",
          color: "#E50218",
          fontSize: "13px",
        }}
      >
        <div style={{ width: "60%" }}>Item</div>
        <div style={{ width: "20%", textAlign: "right" }}>Cost</div>
        <div style={{ width: "20%", textAlign: "right" }}>MRP</div>
      </div>
      {props.children}
    </Paper>
  );
};

function NewPurchasePage() {
  const navigate = useNavigate();
  const { purchaseId, draftId } = useParams();
  const { vendors } = useContext(VendorContext);
  const { inventoryItems, categories, refreshProducts } = useContext(ProductContext);
  const { paymentModes: shopPaymentModes } = useContext(ShopContext);

  const isEditMode = !!purchaseId;
  const isDraftMode = !!draftId;

  const [isAddItemPopupOpen, setIsAddItemPopupOpen] = useState(false);
  const [scannedProductValue, setScannedProductValue] = useState("");

  const [vendor, setVendor] = useState(null);
  const [paidAmount, setPaidAmount] = useState(0);
  const [paymentModes, setPaymentModes] = useState([]);
  const [invoiceNo, setInvoiceNo] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(() => DateTimeHelpers.getLocalISODate());
  const [billDate, setBillDate] = useState(() => DateTimeHelpers.getLocalISODate());
  const [paymentDate, setPaymentDate] = useState(() => DateTimeHelpers.getLocalISODate());

  const [purchaseItems, setPurchaseItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [rate, setRate] = useState(0);
  const [cost, setCost] = useState(0);
  const [margin, setMargin] = useState("");
  const [taxPercent, setTaxPercent] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [removedItemIds, setRemovedItemIds] = useState([]);
  const [itemSearchText, setItemSearchText] = useState("");
  const [productSearchText, setProductSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const invoiceNoInputRef = useRef(null);
  const vendorInputRef = useRef(null);
  const productInputRef = useRef(null);
  const costRef = useRef(null);
  const quantityRef = useRef(null);
  const marginRef = useRef(null);
  const unitPriceRef = useRef(null);
  const taxPercentRef = useRef(null);
  const taxAmountRef = useRef(null);

  const handleSavePurchaseRef = useRef(null);
  const handleSaveDraftRef = useRef(null);

  const handleSelectProduct = useCallback(
    (purchaseItem) => {
      setSelectedProduct(purchaseItem);
      setProductSearchText(purchaseItem?.name || "");
      if (purchaseItem) {
        const itemCost = purchaseItem.cost !== undefined ? purchaseItem.cost : purchaseItem.price || 0;
        const itemMargin =
          purchaseItem.config?.profit_margin !== undefined
            ? purchaseItem.config.profit_margin
            : purchaseItem.profit_margin || "";
        let itemRate = purchaseItem.price || 0;
        if (itemCost > 0 && itemMargin !== "" && Number(itemMargin) > 0) {
          itemRate = itemCost * (1 + Number(itemMargin) / 100);
        }
        setCost(itemCost);
        setMargin(itemMargin);
        setRate(itemRate);
        setTaxPercent(purchaseItem.taxPercentage || 0);
        setTaxAmount(PurchaseHelper.getPurchaseItemTax(purchaseItem.taxPercentage || 0, itemCost * qty));
        setTimeout(() => {
          costRef.current?.focus();
          costRef.current?.select?.();
        }, 100);
      }
    },
    [qty],
  );

  useEffect(() => {
    if (!fetching && !loading) {
      setTimeout(() => {
        invoiceNoInputRef.current?.focus();
        invoiceNoInputRef.current?.select?.();
      }, 200);
    }
  }, [fetching, loading]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (fetching || loading) return;
      if (isScanningBarcode() || event.defaultPrevented) return;

      if (event.key === KEYBOARD_KEYS.F1) {
        event.preventDefault();
        event.stopPropagation();
        productInputRef.current?.focus();
        productInputRef.current?.select?.();
        return;
      }

      if (event.key === KEYBOARD_KEYS.F2) {
        event.preventDefault();
        event.stopPropagation();
        vendorInputRef.current?.focus();
        vendorInputRef.current?.select?.();
        return;
      }

      if (event.key === KEYBOARD_KEYS.F4) {
        event.preventDefault();
        event.stopPropagation();
        handleSavePurchaseRef.current?.();
        return;
      }

      if (event.key === KEYBOARD_KEYS.F5) {
        event.preventDefault();
        event.stopPropagation();
        if (!isEditMode) {
          handleSaveDraftRef.current?.();
        }
        return;
      }
    };

    const unsubscribeFn = eventsEmitter.on(EVENTS.KEY_DOWN, handleKeyDown);
    return unsubscribeFn;
  }, [fetching, loading, isEditMode]);

  function processNewPurchaseScanCode(scannedCode, inventoryItems, handleSelectProduct) {
    if (!scannedCode) return;
    setProductSearchText("");
    if (productInputRef.current) {
      restoreInputElementValue(productInputRef.current, "");
      productInputRef.current.blur?.();
    }
    const matchedItem = (inventoryItems || []).find(
      (item) =>
        (item.ean && String(item.ean).toLowerCase() === scannedCode.toLowerCase()) ||
        (item.plu && String(item.plu).toLowerCase() === scannedCode.toLowerCase()),
    );

    if (matchedItem) {
      handleSelectProduct(matchedItem);
      toast.success(`Scanned: ${matchedItem.name}`);
    } else {
      setScannedProductValue(scannedCode);
      toast.error(`Product not found for barcode: ${scannedCode}`);
    }
  }

  const handlePurchaseScanStart = useCallback(() => {
    setProductSearchText("");
    setScannedProductValue("");
  }, []);

  const handlePurchaseScanCode = useCallback(
    (scannedCode) => {
      processNewPurchaseScanCode(scannedCode, inventoryItems, handleSelectProduct);
    },
    [inventoryItems, handleSelectProduct],
  );

  const purchaseScannerOptions = useMemo(
    () => ({
      isDisabled: fetching || loading || isAddItemPopupOpen,
      onScanStart: handlePurchaseScanStart,
    }),
    [fetching, loading, isAddItemPopupOpen, handlePurchaseScanStart],
  );

  useBarcodeScanner(handlePurchaseScanCode, purchaseScannerOptions);

  const handlePayingAmountChange = (val) => {
    const numVal = val === "" ? "" : Number(val);
    setPaidAmount(numVal);
    if (paymentModes && paymentModes.length > 0) {
      setPaymentModes((prev) =>
        prev.map((pm, idx) => ({
          ...pm,
          amount: idx === 0 ? numVal : 0,
        })),
      );
    }
  };

  const handlePaymentModeAmountChange = (modeId, val) => {
    const numVal = val === "" ? "" : Number(val);
    const updatedModes = paymentModes.map((pm) => (pm.mode_id === modeId ? { ...pm, amount: numVal } : pm));
    setPaymentModes(updatedModes);

    const totalPaid = updatedModes.reduce((sum, pm) => sum + (Number(pm.amount) || 0), 0);
    setPaidAmount(totalPaid);
  };

  const handleAddProduct = () => {
    if (!selectedProduct) {
      toast.error("Please select a product");
      return;
    }

    const numQty = Number(qty) || 0;
    const calculatedTax = PurchaseHelper.getPurchaseItemTax(taxPercent, Number(cost) * numQty);

    const newItem = PurchaseHelper.calculatePurchaseItem({
      item: selectedProduct,
      quantity: numQty,
      cost: cost,
      profit_margin: margin,
      rate: rate,
      taxPercentage: taxPercent,
      manualTaxAmount: taxAmount !== "" && Number(taxAmount) !== calculatedTax ? taxAmount : undefined,
      isExisting: false,
    });

    setPurchaseItems([...purchaseItems, newItem]);
    resetProductInput();
  };

  const resetProductInput = () => {
    setSelectedProduct(null);
    setProductSearchText("");
    setQty(1);
    setRate("");
    setMargin("");
    setTaxPercent("");
    setTaxAmount("");
    setCost("");
    setTimeout(() => {
      productInputRef.current?.focus();
      productInputRef.current?.select?.();
    }, 100);
  };

  const handleProductKeyDown = (e) => {
    if (e.key === KEYBOARD_KEYS.ENTER) {
      if (selectedProduct) {
        e.preventDefault();
        setTimeout(() => {
          costRef.current?.focus();
          costRef.current?.select?.();
        }, 100);
      } else {
        const inputVal = (productSearchText || e.target.value || "").trim();
        if (inputVal) {
          const matchedItem = (inventoryItems || []).find(
            (item) =>
              (item.ean && String(item.ean).toLowerCase() === inputVal.toLowerCase()) ||
              (item.plu && String(item.plu).toLowerCase() === inputVal.toLowerCase()) ||
              (item.name && item.name.toLowerCase() === inputVal.toLowerCase()),
          );
          if (matchedItem) {
            e.preventDefault();
            handleSelectProduct(matchedItem);
            toast.success(`Found: ${matchedItem.name}`);
          }
        }
      }
    }
  };

  const handleFieldKeyDown = (e) => {
    if (e.key === KEYBOARD_KEYS.ENTER) {
      e.preventDefault();
      handleAddProduct();
    }
  };

  const handleFocus = (e) => {
    if (e.target && e.target.select) {
      e.target.select();
    }
  };

  const handleCostChange = (e) => {
    const newCost = e.target.value === "" ? "" : Number(e.target.value);
    setCost(newCost);
    if (newCost !== "" && Number(newCost) > 0 && margin !== "" && margin !== undefined && Number(margin) > 0) {
      setRate(Number(newCost) * (1 + Number(margin) / 100));
    }
    setTaxAmount(PurchaseHelper.getPurchaseItemTax(taxPercent, Number(newCost) * Number(qty)));
  };

  const handleQtyChange = (val) => {
    setQty(val);
    const numQty = Number(val) || 0;
    setTaxAmount(PurchaseHelper.getPurchaseItemTax(taxPercent, Number(cost) * numQty));
  };

  const handleMarginChange = (e) => {
    const newMargin = e.target.value === "" ? "" : Number(e.target.value);
    setMargin(newMargin);
    if (cost !== "" && Number(cost) > 0 && newMargin !== "" && !isNaN(Number(newMargin)) && Number(newMargin) > 0) {
      setRate(Number(cost) * (1 + Number(newMargin) / 100));
    } else if (selectedProduct) {
      setRate(selectedProduct.price || 0);
    }
  };

  const handleRateChange = (e) => {
    const newRate = e.target.value === "" ? "" : Number(e.target.value);
    setRate(newRate);
  };

  const handleTaxPercentChange = (e) => {
    const newTaxPercent = Number(e.target.value);
    setTaxPercent(newTaxPercent);
    setTaxAmount(PurchaseHelper.getPurchaseItemTax(newTaxPercent, Number(cost) * Number(qty)));
  };

  const handleTaxAmountChange = (e) => {
    const newTaxAmount = Number(e.target.value);
    setTaxAmount(newTaxAmount);
    if (Number(cost) * Number(qty) !== 0) {
      setTaxPercent((newTaxAmount * 100) / (Number(cost) * Number(qty)));
    }
  };

  const handleUpdateRowProduct = (index, item) => {
    if (!item) return;
    const updated = [...purchaseItems];
    const itemCost = item.cost !== undefined ? item.cost : item.price || 0;
    const itemMargin = item?.config?.profit_margin !== undefined ? item.config.profit_margin : item.profit_margin || "";
    let itemRate = item.price || 0;
    if (itemCost > 0 && itemMargin !== "" && Number(itemMargin) > 0) {
      itemRate = itemCost * (1 + Number(itemMargin) / 100);
    }
    updated[index] = PurchaseHelper.calculatePurchaseItem({
      id: updated[index].id,
      item,
      quantity: updated[index].quantity,
      cost: itemCost,
      profit_margin: itemMargin,
      rate: itemRate,
      discount: updated[index].discount,
      taxPercentage: item.tax || 0,
    });
    setPurchaseItems(updated);
  };

  const updateRowQuantity = (index, value) => {
    const updated = [...purchaseItems];
    const numQty = value === KEYBOARD_KEYS.DOT ? 0 : Number(value);
    updated[index] = PurchaseHelper.calculatePurchaseItem({
      id: updated[index].id,
      item: updated[index].item,
      quantity: numQty,
      cost: updated[index].cost,
      profit_margin: updated[index].profit_margin,
      rate: updated[index].rate,
      discount: updated[index].discount,
      taxPercentage: updated[index].taxPercentage,
    });
    setPurchaseItems(updated);
  };

  const updateRowCost = (index, value) => {
    const updated = [...purchaseItems];
    const costVal = value === "" ? "" : Number(value);
    const marginVal = updated[index].profit_margin;
    let rateVal = updated[index].rate;
    if (costVal !== "" && Number(costVal) > 0 && marginVal !== "" && marginVal !== undefined && Number(marginVal) > 0) {
      rateVal = Number(costVal) * (1 + Number(marginVal) / 100);
    }
    updated[index] = PurchaseHelper.calculatePurchaseItem({
      id: updated[index].id,
      item: updated[index].item,
      quantity: updated[index].quantity,
      cost: costVal,
      profit_margin: marginVal,
      rate: rateVal,
      discount: updated[index].discount,
      taxPercentage: updated[index].taxPercentage,
    });
    setPurchaseItems(updated);
  };

  const updateRowMargin = (index, value) => {
    const updated = [...purchaseItems];
    const marginVal = value === "" ? "" : Number(value);
    const costVal = updated[index].cost;
    let rateVal = updated[index].rate;
    if (
      costVal !== "" &&
      Number(costVal) > 0 &&
      marginVal !== "" &&
      !isNaN(Number(marginVal)) &&
      Number(marginVal) > 0
    ) {
      rateVal = Number(costVal) * (1 + Number(marginVal) / 100);
    } else if (updated[index].item) {
      rateVal = updated[index].item.price || 0;
    }
    updated[index] = PurchaseHelper.calculatePurchaseItem({
      id: updated[index].id,
      item: updated[index].item,
      quantity: updated[index].quantity,
      cost: costVal,
      profit_margin: marginVal,
      rate: rateVal,
      discount: updated[index].discount,
      taxPercentage: updated[index].taxPercentage,
    });
    setPurchaseItems(updated);
  };

  const updateRowRate = (index, value) => {
    const updated = [...purchaseItems];
    const rateVal = value === "" ? "" : Number(value);
    updated[index] = PurchaseHelper.calculatePurchaseItem({
      id: updated[index].id,
      item: updated[index].item,
      quantity: updated[index].quantity,
      cost: updated[index].cost,
      profit_margin: updated[index].profit_margin,
      rate: rateVal,
      discount: updated[index].discount,
      taxPercentage: updated[index].taxPercentage,
    });
    setPurchaseItems(updated);
  };

  const updateRowTaxPercent = (index, value) => {
    const updated = [...purchaseItems];
    updated[index] = PurchaseHelper.calculatePurchaseItem({
      id: updated[index].id,
      item: updated[index].item,
      quantity: updated[index].quantity,
      cost: updated[index].cost,
      profit_margin: updated[index].profit_margin,
      rate: updated[index].rate,
      discount: updated[index].discount,
      taxPercentage: value,
    });
    setPurchaseItems(updated);
  };

  const updateRowTaxAmount = (index, value) => {
    const updated = [...purchaseItems];
    updated[index] = PurchaseHelper.calculatePurchaseItem({
      id: updated[index].id,
      item: updated[index].item,
      quantity: updated[index].quantity,
      cost: updated[index].cost,
      profit_margin: updated[index].profit_margin,
      rate: updated[index].rate,
      discount: updated[index].discount,
      taxPercentage: updated[index].taxPercentage,
      manualTaxAmount: value,
    });
    setPurchaseItems(updated);
  };

  const handleRemoveItem = (index) => {
    const item = purchaseItems[index];
    if (item.id) {
      setRemovedItemIds([...removedItemIds, item.id]);
    }
    const updated = [...purchaseItems];
    updated.splice(index, 1);
    setPurchaseItems(updated);
  };

  const totals = PurchaseHelper.calculateTotals(purchaseItems);

  async function handleUpdateExistingPurchase(purchaseId, payload, totals) {
    const {
      invoiceNo,
      vendor,
      purchaseDate,
      billDate,
      paymentDate,
      purchaseItems,
      paidAmount,
      paymentModes,
      removedItemIds,
    } = payload;
    const existingItems = purchaseItems.filter((item) => item.id);
    const newItems = purchaseItems.filter((item) => !item.id);

    const response = await PurchaseHelper.updatePurchase(purchaseId, {
      invoiceNo,
      vendor,
      purchaseDate,
      billDate,
      paymentDate,
      items: existingItems,
      newItems: newItems,
      removeItems: removedItemIds,
      totalAmount: totals.total,
      paidAmount,
      paymentModes,
    });

    const mappedItems = PurchaseHelper.mapServerPurchaseItems(purchaseItems, response?.items || []);

    const updatedPurchaseData = {
      invoiceNo,
      vendor,
      purchaseDate,
      billDate,
      paymentDate,
      items: mappedItems,
      paidAmount,
      paymentModes,
      id: Number(purchaseId) || null,
    };

    try {
      await PurchaseHelper.saveOrUpdateSavedPurchase(purchaseId, updatedPurchaseData);
      await ProductHelper.updateLocalProductsFromPurchase(mappedItems);
      await refreshProducts(false, true);
    } catch (localError) {
      console.error("Failed to update local purchase database record after API update:", localError);
    }

    toast.success("Purchase updated successfully");
  }

  async function handleCreateNewPurchase(isDraftMode, draftId, payload) {
    const { invoiceNo, vendor, purchaseDate, billDate, paymentDate, purchaseItems, paidAmount, paymentModes } = payload;

    const response = await PurchaseHelper.savePurchase({
      invoiceNo,
      vendor,
      purchaseDate,
      billDate,
      paymentDate,
      purchaseItems,
      paidAmount,
      paymentModes,
    });

    const serverItems = response?.items || [];
    const mappedItems = PurchaseHelper.mapServerPurchaseItems(purchaseItems, serverItems);

    const purchaseData = {
      invoiceNo,
      vendor,
      purchaseDate,
      billDate,
      paymentDate,
      items: mappedItems,
      paidAmount,
      paymentModes,
      id: Number(response?.id) || null,
    };

    try {
      if (isDraftMode && draftId) {
        await PurchaseHelper.deleteDraftPurchase(draftId);
      }
      await PurchaseHelper.saveOrUpdateSavedPurchase(response?.id, purchaseData);
      await ProductHelper.updateLocalProductsFromPurchase(mappedItems);
      await refreshProducts(false, true);
    } catch (localError) {
      console.error("Failed to save local purchase database record after API creation:", localError);
    }

    toast.success("Purchase added successfully");
  }

  const handleSavePurchase = async () => {
    if (!invoiceNo) {
      toast.error("Please enter invoice number");
      return;
    }

    if (purchaseItems.length === 0) {
      toast.error("Please add at least one product");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        invoiceNo,
        vendor,
        purchaseDate,
        billDate,
        paymentDate,
        purchaseItems,
        paidAmount,
        paymentModes,
        removedItemIds,
      };

      if (isEditMode) {
        await handleUpdateExistingPurchase(purchaseId, payload, totals);
      } else {
        await handleCreateNewPurchase(isDraftMode, draftId, payload);
      }
      navigate(ROUTES.PURCHASE);
    } catch (error) {
      toast.error(error.message || "Failed to save purchase");
    } finally {
      setLoading(false);
    }
  };

  handleSavePurchaseRef.current = handleSavePurchase;

  const handleSaveDraft = async () => {
    if (!invoiceNo && purchaseItems.length === 0) {
      toast.error("Please enter invoice number or add products to save as draft");
      return;
    }

    setLoading(true);
    try {
      const draftData = {
        invoiceNo,
        vendor,
        purchaseDate,
        billDate,
        paymentDate,
        items: purchaseItems,
        paidAmount,
        paymentModes,
      };

      if (isDraftMode) {
        await PurchaseHelper.updateDraftPurchase(draftId, draftData);
        toast.success("Draft updated successfully");
      } else {
        await PurchaseHelper.saveDraftPurchase(draftData);
        toast.success("Purchase saved as draft");
      }
      navigate(ROUTES.PURCHASE);
    } catch (error) {
      toast.error(error.message || "Failed to save draft");
    } finally {
      setLoading(false);
    }
  };

  handleSaveDraftRef.current = handleSaveDraft;

  const lastFetchedIdRef = useRef(null);

  useEffect(() => {
    const currentId = isEditMode ? purchaseId : isDraftMode ? draftId : null;
    if (currentId && lastFetchedIdRef.current !== currentId) {
      lastFetchedIdRef.current = currentId;
      const fetchPurchaseDetails = async () => {
        setFetching(true);
        try {
          let data = null;
          if (isEditMode) {
            data = await PurchaseHelper.getLocalPurchase(purchaseId);
          } else if (isDraftMode) {
            data = await PurchaseHelper.getDraftPurchase(draftId);
          }

          if (data) {
            PurchaseHelper.applyPurchaseDetailsData(data, isDraftMode, vendors, shopPaymentModes, {
              setInvoiceNo,
              setVendor,
              setPaidAmount,
              setPaymentModes,
              setPurchaseDate,
              setBillDate,
              setPaymentDate,
              setPurchaseItems,
            });
          }
        } catch (error) {
          console.error("Failed to fetch purchase details:", error);
          toast.error("Failed to load purchase details");
        } finally {
          setFetching(false);
        }
      };
      fetchPurchaseDetails();
    }
  }, [isEditMode, purchaseId, isDraftMode, draftId, vendors, shopPaymentModes]);

  useEffect(() => {
    if (Array.isArray(shopPaymentModes) && shopPaymentModes.length > 0) {
      setPaymentModes((prev) => {
        if (!prev || prev.length === 0) {
          return shopPaymentModes.map((pm) => ({
            mode_id: pm.id,
            name: pm.name,
            amount: 0,
          }));
        }
        const prevAmountMap = new Map(prev.map((p) => [p.mode_id, p.amount]));
        return shopPaymentModes.map((pm) => ({
          mode_id: pm.id,
          name: pm.name,
          amount: prevAmountMap.has(pm.id) ? prevAmountMap.get(pm.id) : 0,
        }));
      });
    }
  }, [shopPaymentModes]);

  if (fetching || loading) {
    return (
      <Grid container style={{ padding: "20px" }}>
        <SkeletonLoading isOpen={true} count={15} height={50} xs={12} />
      </Grid>
    );
  }

  return (
    <Grid style={{ padding: "10px", height: "100%" }}>
      <Grid className={styles.container}>
        <Grid item xs={12} className={styles.header}>
          <h2>{isEditMode ? "Edit Purchase" : isDraftMode ? "Complete Draft Purchase" : "New Purchase"}</h2>
          <div className={styles.headerActions}>
            <ButtonGray onClick={() => navigate(isDraftMode ? ROUTES.DRAFT_PURCHASES : ROUTES.PURCHASE)}>
              Cancel
            </ButtonGray>
            {!isEditMode && (
              <ButtonGray onClick={handleSaveDraft} style={{ backgroundColor: "#f0f0f0", color: "#333" }}>
                Save as Draft (F5)
              </ButtonGray>
            )}
            <ButtonRed onClick={handleSavePurchase}>Save Purchase (F4)</ButtonRed>
          </div>
        </Grid>

        {/* Purchase Details Section */}
        <Grid item xs={12}>
          <Grid className={styles.sectionPaper}>
            <Grid container alignItems="center">
              <Grid item xs={6} md={3} className={styles.inputField}>
                <TextField
                  inputRef={invoiceNoInputRef}
                  label="Invoice Number"
                  fullWidth
                  size="small"
                  value={invoiceNo}
                  onChange={(e) => setInvoiceNo(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === KEYBOARD_KEYS.ENTER) {
                      e.preventDefault();
                      setTimeout(() => {
                        vendorInputRef.current?.focus();
                        vendorInputRef.current?.select?.();
                      }, 100);
                    }
                  }}
                  onFocus={handleFocus}
                  onClick={handleFocus}
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "4px",
                    "& .MuiOutlinedInput-root input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px #ffffff inset !important",
                      WebkitTextFillColor: "#333 !important",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3} className={styles.inputField}>
                <Autocomplete
                  options={(vendors || []).filter((v) => v.is_active)}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={vendor}
                  onChange={(e, val) => setVendor(val)}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputRef={vendorInputRef}
                      label="Vendor (F2)"
                      size="small"
                      onKeyDown={(e) => {
                        if (e.key === KEYBOARD_KEYS.ENTER) {
                          e.preventDefault();
                          setTimeout(() => {
                            productInputRef.current?.focus();
                            productInputRef.current?.select?.();
                          }, 100);
                        }
                      }}
                    />
                  )}
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "4px",
                    "& .MuiOutlinedInput-root input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px #ffffff inset !important",
                      WebkitTextFillColor: "#333 !important",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={6} md={3} className={styles.inputField}>
                <TextField
                  label="Paying Amount"
                  type="number"
                  fullWidth
                  size="small"
                  value={paidAmount}
                  onChange={(e) => handlePayingAmountChange(e.target.value)}
                  onFocus={handleFocus}
                  onClick={handleFocus}
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "4px",
                    "& .MuiOutlinedInput-root input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px #ffffff inset !important",
                      WebkitTextFillColor: "#333 !important",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3} className={styles.inputField}>
                <TextField
                  label="Vendor Balance"
                  fullWidth
                  size="small"
                  disabled
                  value={NumberFormatHelper.customerAmountFormat(vendor?.balance || 0)}
                  className={styles.readOnlyField}
                  InputProps={{
                    sx: { color: vendor?.balance < 0 ? "red" : "green", fontWeight: 600 },
                  }}
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "4px",
                    "& .MuiOutlinedInput-root input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px #ffffff inset !important",
                      WebkitTextFillColor: "#333 !important",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={6} md={3} className={styles.inputField}>
                <TextField
                  placeholder="Search purchase product"
                  fullWidth
                  size="small"
                  value={itemSearchText}
                  onChange={(e) => setItemSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <img src={SearchIcon} alt="Search" style={{ width: 16, height: 16, opacity: 0.5 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "4px",
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "15px !important",
                    },
                    "& .MuiOutlinedInput-root input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px #ffffff inset !important",
                      WebkitTextFillColor: "#333 !important",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3} className={styles.inputField}>
                <TextField
                  label="Purchase Date"
                  type="date"
                  fullWidth
                  size="small"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "4px",
                    "& .MuiOutlinedInput-root input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px #ffffff inset !important",
                      WebkitTextFillColor: "#333 !important",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3} className={styles.inputField}>
                <TextField
                  label="Bill Date"
                  type="date"
                  fullWidth
                  size="small"
                  value={billDate}
                  onChange={(e) => setBillDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "4px",
                    "& .MuiOutlinedInput-root input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px #ffffff inset !important",
                      WebkitTextFillColor: "#333 !important",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3} className={styles.inputField}>
                <TextField
                  label="Payment Date"
                  type="date"
                  fullWidth
                  size="small"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "4px",
                    "& .MuiOutlinedInput-root input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px #ffffff inset !important",
                      WebkitTextFillColor: "#333 !important",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Product Add Table Section */}
        <Grid item xs={12}>
          <div className={styles.tableWrapper}>
            <div className={styles.tableContainer}>
              <table className={styles.productTable}>
                <thead>
                  <tr>
                    <th style={{ width: "60px", borderTopLeftRadius: "10px" }}>Sl No</th>
                    <th>Product</th>
                    <th style={{ width: "100px" }}>Cost</th>
                    <th style={{ width: "80px" }}>Qty</th>
                    <th style={{ width: "90px" }}>Margin (%)</th>
                    <th style={{ width: "125px" }}>Selling Price</th>
                    <th style={{ width: "80px" }}>Tax %</th>
                    <th style={{ width: "100px" }}>Tax Amount</th>
                    <th style={{ width: "100px" }}>Total</th>
                    <th style={{ width: "60px", borderTopRightRadius: "10px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseItems
                    .filter((item) => {
                      if (!itemSearchText) return true;
                      const search = itemSearchText.toLowerCase();
                      const itemName = (item.item?.name || "").toLowerCase();
                      const itemEan = (item.item?.ean || "").toLowerCase();
                      const itemPlu = String(item.item?.plu || "").toLowerCase();
                      return itemName.includes(search) || itemEan.includes(search) || itemPlu.includes(search);
                    })
                    .map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td style={{ padding: 0 }}>
                          <Autocomplete
                            options={inventoryItems}
                            getOptionLabel={(option) => option.name}
                            filterOptions={customFilterOptions}
                            value={inventoryItems.find((i) => i.id === item.item_id) || null}
                            onChange={(e, item) => {
                              if (isScanningBarcode()) return;
                              handleUpdateRowProduct(index, item);
                            }}
                            onInputChange={(e, val) => {
                              if (isScanningBarcode()) return;
                              setScannedProductValue(val);
                            }}
                            disableClearable
                            autoHighlight
                            noOptionsText={
                              <div style={{ display: "flex", gap: "2px", alignItems: "center" }}>
                                <span style={{ color: "#777" }}>No options</span>
                                <span
                                  style={{
                                    color: "#E50218",
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                    fontSize: "13px",
                                  }}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    document.activeElement?.blur();
                                    setIsAddItemPopupOpen(true);
                                  }}
                                >
                                  Create item
                                </span>
                              </div>
                            }
                            PaperComponent={DropdownPaper}
                            renderOption={(props, option, state) => (
                              <li
                                {...props}
                                key={`${option.id}-${state.index}`}
                                style={{ ...props.style, fontSize: "13px", display: "flex", width: "100%" }}
                              >
                                <div
                                  style={{
                                    width: "60%",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {option.name}
                                </div>
                                <div style={{ width: "20%", textAlign: "right" }}>
                                  {option.cost !== undefined && option.cost !== null
                                    ? NumberFormatHelper.roundOff(option.cost)
                                    : "-"}
                                </div>
                                <div style={{ width: "20%", textAlign: "right" }}>
                                  {option.mrp !== undefined && option.mrp !== null
                                    ? NumberFormatHelper.roundOff(option.mrp)
                                    : option.price !== undefined && option.price !== null
                                      ? NumberFormatHelper.roundOff(option.price)
                                      : "-"}
                                </div>
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                InputProps={{ ...params.InputProps, disableUnderline: true }}
                                sx={{
                                  "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                                  width: "100%",
                                }}
                              />
                            )}
                            sx={{
                              "& .MuiAutocomplete-popupIndicator": { visibility: "hidden" },
                              "&:hover .MuiAutocomplete-popupIndicator": { visibility: "visible" },
                              "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                              width: "100%",
                            }}
                          />
                        </td>

                        <td style={{ padding: 0 }}>
                          <TextField
                            type="number"
                            variant="standard"
                            InputProps={{ disableUnderline: true }}
                            value={NumberFormatHelper.amountRoundOff(item.cost)}
                            onChange={(e) => updateRowCost(index, e.target.value)}
                            onFocus={handleFocus}
                            onClick={handleFocus}
                            sx={{
                              "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                              width: "100%",
                            }}
                          />
                        </td>

                        <td style={{ padding: 0 }}>
                          <QtyTextField
                            value={item.quantity}
                            onCommit={(val) => updateRowQuantity(index, val)}
                            onChange={(val) => updateRowQuantity(index, val)}
                            sx={{
                              "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                              width: "100%",
                            }}
                          />
                        </td>

                        <td style={{ padding: 0 }}>
                          <TextField
                            type="number"
                            variant="standard"
                            InputProps={{ disableUnderline: true }}
                            value={item.profit_margin !== undefined ? item.profit_margin : ""}
                            onChange={(e) => updateRowMargin(index, e.target.value)}
                            onFocus={handleFocus}
                            onClick={handleFocus}
                            sx={{
                              "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                              width: "100%",
                            }}
                          />
                        </td>

                        <td style={{ padding: 0 }}>
                          <TextField
                            type="number"
                            variant="standard"
                            InputProps={{ disableUnderline: true }}
                            value={NumberFormatHelper.amountRoundOff(item.rate)}
                            onChange={(e) => updateRowRate(index, e.target.value)}
                            onFocus={handleFocus}
                            onClick={handleFocus}
                            sx={{
                              "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                              width: "100%",
                            }}
                          />
                        </td>

                        <td style={{ padding: 0 }}>
                          <TextField
                            type="number"
                            variant="standard"
                            InputProps={{ disableUnderline: true }}
                            value={NumberFormatHelper.amountRoundOff(item.taxPercentage || 0)}
                            onChange={(e) => updateRowTaxPercent(index, e.target.value)}
                            onFocus={handleFocus}
                            onClick={handleFocus}
                            sx={{
                              "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                              width: "100%",
                            }}
                          />
                        </td>
                        <td style={{ padding: 0 }}>
                          <TextField
                            type="number"
                            variant="standard"
                            InputProps={{ disableUnderline: true }}
                            value={NumberFormatHelper.amountExtendedRoundOff(item.tax)}
                            onChange={(e) => updateRowTaxAmount(index, e.target.value)}
                            onFocus={handleFocus}
                            onClick={handleFocus}
                            sx={{
                              "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                              width: "100%",
                            }}
                          />
                        </td>
                        <td>{NumberFormatHelper.roundOff(item.total)}</td>
                        <td>
                          <IconButton onClick={() => handleRemoveItem(index)}>
                            <DeleteIcon sx={{ color: "#E50218", fontSize: "18px" }} />
                          </IconButton>
                        </td>
                      </tr>
                    ))}

                  {/* Input Row */}
                  <tr className={styles.inputRow}>
                    <td>{purchaseItems.length + 1}</td>
                    <td style={{ padding: 0 }}>
                      <Autocomplete
                        key={selectedProduct ? selectedProduct.id : "new-purchase-item"}
                        options={inventoryItems}
                        getOptionLabel={(option) => option.name}
                        filterOptions={customFilterOptions}
                        value={selectedProduct}
                        inputValue={productSearchText || ""}
                        onChange={(e, val) => {
                          if (isScanningBarcode()) return;
                          setProductSearchText(val?.name || "");
                          handleSelectProduct(val);
                        }}
                        onInputChange={(e, val) => {
                          if (isScanningBarcode()) return;
                          setProductSearchText(val);
                          setScannedProductValue(val);
                        }}
                        onKeyDown={(e) => {
                          if (isScanningBarcode() || e.defaultPrevented || e.isHandled) {
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                          }
                          handleProductKeyDown(e);
                        }}
                        clearOnBlur={false}
                        autoHighlight
                        noOptionsText={
                          <div style={{ display: "flex", gap: "2px", alignItems: "center" }}>
                            <span style={{ color: "#777" }}>No options</span>
                            <span
                              style={{
                                color: "#E50218",
                                cursor: "pointer",
                                textDecoration: "underline",
                                fontSize: "13px",
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                document.activeElement?.blur();
                                setIsAddItemPopupOpen(true);
                              }}
                            >
                              Create item
                            </span>
                          </div>
                        }
                        PaperComponent={DropdownPaper}
                        renderOption={(props, option, state) => (
                          <li
                            {...props}
                            key={`${option.id}-${state.index}`}
                            style={{ ...props.style, fontSize: "13px", display: "flex", width: "100%" }}
                          >
                            <div
                              style={{
                                width: "60%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {option.name}
                            </div>
                            <div style={{ width: "20%", textAlign: "right" }}>
                              {option.cost !== undefined && option.cost !== null
                                ? NumberFormatHelper.roundOff(option.cost)
                                : "-"}
                            </div>
                            <div style={{ width: "20%", textAlign: "right" }}>
                              {option.mrp !== undefined && option.mrp !== null
                                ? NumberFormatHelper.roundOff(option.mrp)
                                : option.price !== undefined && option.price !== null
                                  ? NumberFormatHelper.roundOff(option.price)
                                  : "-"}
                            </div>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            inputRef={productInputRef}
                            placeholder="(F1) Search Product"
                            variant="standard"
                            InputProps={{ ...params.InputProps, disableUnderline: true }}
                            inputProps={params.inputProps}
                            sx={{
                              "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                              width: "100%",
                            }}
                          />
                        )}
                      />
                    </td>

                    <td style={{ padding: 0 }}>
                      <TextField
                        type="number"
                        inputRef={costRef}
                        value={NumberFormatHelper.amountRoundOff(cost)}
                        onChange={handleCostChange}
                        onKeyDown={handleFieldKeyDown}
                        onFocus={handleFocus}
                        onClick={handleFocus}
                        placeholder="Cost"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{
                          "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                          width: "100%",
                        }}
                      />
                    </td>

                    <td style={{ padding: 0 }}>
                      <QtyTextField
                        inputRef={quantityRef}
                        value={qty}
                        onChange={handleQtyChange}
                        onKeyDown={handleFieldKeyDown}
                        placeholder="Qty"
                        sx={{
                          "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                          width: "100%",
                        }}
                      />
                    </td>

                    <td style={{ padding: 0 }}>
                      <TextField
                        type="number"
                        inputRef={marginRef}
                        value={NumberFormatHelper.amountRoundOff(margin)}
                        onChange={handleMarginChange}
                        onKeyDown={handleFieldKeyDown}
                        onFocus={handleFocus}
                        onClick={handleFocus}
                        placeholder="Margin"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{
                          "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                          width: "100%",
                        }}
                      />
                    </td>

                    <td style={{ padding: 0 }}>
                      <TextField
                        type="number"
                        inputRef={unitPriceRef}
                        value={NumberFormatHelper.amountRoundOff(rate)}
                        onChange={handleRateChange}
                        onKeyDown={handleFieldKeyDown}
                        onFocus={handleFocus}
                        onClick={handleFocus}
                        placeholder="Selling Price"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{
                          "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                          width: "100%",
                        }}
                      />
                    </td>

                    <td style={{ padding: 0 }}>
                      <TextField
                        type="number"
                        inputRef={taxPercentRef}
                        value={taxPercent}
                        onChange={handleTaxPercentChange}
                        onKeyDown={handleFieldKeyDown}
                        onFocus={handleFocus}
                        onClick={handleFocus}
                        placeholder="Tax %"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{
                          "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                          width: "100%",
                        }}
                      />
                    </td>
                    <td style={{ padding: 0 }}>
                      <TextField
                        type="number"
                        inputRef={taxAmountRef}
                        value={NumberFormatHelper.amountRoundOff(taxAmount)}
                        onChange={handleTaxAmountChange}
                        onKeyDown={handleFieldKeyDown}
                        onFocus={handleFocus}
                        onClick={handleFocus}
                        placeholder="Tax"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{
                          "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                          width: "100%",
                        }}
                      />
                    </td>
                    <td>
                      {selectedProduct && qty
                        ? NumberFormatHelper.amountRoundOff(
                            Number(cost) * Number(qty) + (Number(cost) * Number(qty) * Number(taxPercent)) / 100,
                          )
                        : ""}
                    </td>
                    <td>
                      <IconButton onClick={handleAddProduct} disabled={!selectedProduct}>
                        <AddIcon sx={{ color: "#4CAF50", fontSize: "18px" }} />
                      </IconButton>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className={styles.tableFooterSection}>
              <table className={styles.productTable}>
                <tfoot>
                  <tr>
                    <td
                      colSpan="2"
                      style={{
                        textAlign: "left",
                        fontWeight: "bold",
                        paddingLeft: "15px",
                        borderBottomLeftRadius: "10px",
                        borderBottom: "none",
                        borderRight: "none",
                        borderLeft: "none",
                      }}
                    >
                      Total
                    </td>
                    <td style={{ width: "100px", fontWeight: "bold", borderBottom: "none", borderRight: "none" }}></td>
                    <td style={{ width: "80px", fontWeight: "bold", borderBottom: "none", borderRight: "none" }}>
                      {totals.qty}
                    </td>
                    <td style={{ width: "90px", fontWeight: "bold", borderBottom: "none", borderRight: "none" }}></td>
                    <td style={{ width: "125px", fontWeight: "bold", borderBottom: "none", borderRight: "none" }}></td>
                    <td style={{ width: "80px", fontWeight: "bold", borderBottom: "none", borderRight: "none" }}></td>
                    <td style={{ width: "100px", fontWeight: "bold", borderBottom: "none", borderRight: "none" }}>
                      {NumberFormatHelper.roundOff(totals.tax)}
                    </td>
                    <td
                      style={{
                        fontWeight: "bold",
                        width: "100px",
                        borderBottom: "none",
                        borderRight: "none",
                      }}
                    >
                      {NumberFormatHelper.roundOff(totals.total)}
                    </td>
                    <td
                      style={{
                        width: "60px",
                        borderBottomRightRadius: "10px",
                        borderBottom: "none",
                        borderRight: "none",
                      }}
                    ></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </Grid>

        {/* Payment Modes Section */}
        <Grid item xs={12}>
          <Grid className={styles.sectionPaper}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs={12}>
                <h4 style={{ margin: "2px 6px 6px 6px", fontSize: "14px", color: "#2b2b2b", fontWeight: 600 }}>
                  Payment Information
                </h4>
              </Grid>
              {paymentModes && paymentModes.length > 0 ? (
                paymentModes.map((pm) => (
                  <Grid item xs={6} sm={4} md={2} key={pm.mode_id} className={styles.inputField}>
                    <TextField
                      label={pm.name}
                      type="number"
                      fullWidth
                      size="small"
                      value={pm.amount === 0 || pm.amount === "0" ? "" : pm.amount}
                      placeholder="0"
                      onChange={(e) => handlePaymentModeAmountChange(pm.mode_id, e.target.value)}
                      onFocus={handleFocus}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "4px",
                        "& .MuiOutlinedInput-root input:-webkit-autofill": {
                          WebkitBoxShadow: "0 0 0 1000px #ffffff inset !important",
                          WebkitTextFillColor: "#333 !important",
                        },
                      }}
                    />
                  </Grid>
                ))
              ) : (
                <Grid item xs={12} style={{ padding: "6px 10px", color: "#666", fontSize: "13px" }}>
                  No payment modes available
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <AddItemPopup
        open={isAddItemPopupOpen}
        onClose={() => {
          setIsAddItemPopupOpen(false);
          setScannedProductValue("");
        }}
        categories={categories}
        initialEan={scannedProductValue}
      />
    </Grid>
  );
}

export default NewPurchasePage;
